import type { AbilityDef } from "../../sim";
import { BASE_KIT } from "./base";
import { BLINK } from "./blink";
import { BACK_TREE, COMBO_NODES, COMBO_TREE, DIRECTION_NODES, DOWN_TREE, FINALE_FX, FINALE_IDS } from "./comboTree";
import { LIMB_REACH } from "./reach";
import { rootMotionFor } from "./rootMotion";
import { DASH_STRIKE } from "./dash-strike";
import { FLIP_KICK } from "./flip-kick";
import { BACK_RUN } from "./back-run";
import { MAGIC_SHOT } from "./magic-shot";
import { TAUNTS } from "./taunts";
import { GRAB_WHIFF, SHOULDER_THROW } from "./throw";
import { RISING_STRIKE } from "./rising-strike";
import { VANISH } from "./vanish";

/**
 * Зона удара — докуда реально доходит бьющая кость клипа (консультант 05.11.2026: «удар засчитывается, когда рука ещё
 * в 50–128 px от тела — „удар квадратиком нифига не удар“»; обмер — content/abilities/reach.ts): передний край не дальше
 * кости плюс GUARD (руки соперника в защите выставлены вперёд). Только укорачивает; вплотную удар достаёт всегда —
 * бойцы не сходятся ближе 76 px, поэтому край не ближе POINT_BLANK.
 */
const GUARD = 7;
/** Ногой — без надбавки: нога бьёт по корпусу и ногам, руки соперника в защите её не встречают. */
const GUARD_LEG = 0;
const POINT_BLANK = 52;
const reached = (ability: AbilityDef): AbilityDef => {
  const reach = LIMB_REACH[ability.id];
  const box = ability.hitbox;
  if (!reach || !box) return ability;
  const guard = reach.limb.endsWith("ToeBase") || reach.limb.endsWith("Leg") ? GUARD_LEG : GUARD;
  const front = Math.min(box.x + box.w, Math.max(POINT_BLANK, Math.round(reach.forward + guard)));
  const width = Math.min(box.w, front);
  // в прыжке зона удара — на высоте кости клипа (вверх от стоп), а не где была: клип и подброс разные
  const y = ability.input.air ? -Math.round(reach.up + box.h / 2) : box.y;
  return { ...ability, hitbox: { ...box, x: front - width, w: width, y } };
};

/** Приём с ходом корня клипа (если у его клипа он есть): сдвиг мира совпадает с шагом в клипе. */
const rooted = (ability: AbilityDef): AbilityDef => {
  const motion = rootMotionFor(ability);
  return motion ? { ...ability, motion } : ability;
};

/** Ветки дерева комбо и знак финала (content/abilities/comboTree.ts). */
const branched = (ability: AbilityDef): AbilityDef => {
  const branches = COMBO_TREE[ability.id];
  const backBranches = BACK_TREE[ability.id];
  const downBranches = DOWN_TREE[ability.id];
  const finaleHit = FINALE_FX[ability.id];
  return {
    ...ability,
    ...(branches ? { branches } : {}),
    ...(backBranches ? { backBranches, holdsGround: true } : {}),
    ...(downBranches ? { downBranches } : {}),
    ...(FINALE_IDS.has(ability.id) ? { finale: true } : {}),
    ...(finaleHit ? { fx: { ...ability.fx, hit: finaleHit } } : {}),
  };
};

/** Все способности по id. Новая способность — свой файл и одна строка здесь (docs/design/abilities.md). */
export const ABILITIES: ReadonlyMap<string, AbilityDef> = new Map(
  [
    ...BASE_KIT,
    ...COMBO_NODES,
    ...DIRECTION_NODES,
    DASH_STRIKE,
    FLIP_KICK,
    RISING_STRIKE,
    MAGIC_SHOT,
    BACK_RUN,
    ...TAUNTS,
    SHOULDER_THROW,
    GRAB_WHIFF,
    BLINK,
    VANISH,
  ].map((ability) => [ability.id, reached(rooted(branched(ability)))]),
);

export { COMBO_EXAMPLES } from "./comboTree";

export const BASE_KIT_IDS: readonly string[] = [...BASE_KIT, ...COMBO_NODES, ...DIRECTION_NODES].map(
  (ability) => ability.id,
);
